import { useTranslation } from 'react-i18next'
import { useContext, useMemo, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useQuery, useMutation } from 'react-query'
import { Table } from 'antd'

import { UserContext } from '../../hooks/UserContext'
import { PageView } from '../../components/PageView'
import { PageTopBar } from '../../components/PageTopBar'
import { Button } from '../../components/Button'
import { Notification } from '../../components/Notification'
import { Loader } from '../../components/Loader'
import { FormattedDate } from '../../components/FormattedDate'
import { getUberBatch, retryUberBatch } from '../../services'
import { fetchWarehouse } from '../../services/warehouseSevices'
import { SHIPPED_ORDER_DETAIL, UBER_BATCHES } from '../../navigation/constants'
import { UberBatchStatusPill } from './UberBatchStatusPill'
import { RETRYABLE_FAILURE_REASON } from './uberBatchStatus'

const T_PREFIX = 'uber_batches'

const REFETCH_INTERVAL_MS = 30000

export const UberBatchDetail = () => {
    const { i18n } = useTranslation()
    const { user } = useContext(UserContext)
    const { warehouse_id, id } = useParams()
    const history = useHistory()

    const hasAccess = !!user?.permissions?.index_uber_batches

    const { data: warehouse } = useQuery(
        ['warehouse', warehouse_id],
        () => fetchWarehouse(warehouse_id),
        { keepPreviousData: true, refetchOnWindowFocus: false }
    )

    const {
        data: batch,
        isLoading,
        isError,
        error,
        refetch,
    } = useQuery(
        ['uber_batch', id],
        () => getUberBatch(id),
        {
            keepPreviousData: true,
            refetchInterval: REFETCH_INTERVAL_MS,
            refetchOnWindowFocus: false,
            enabled: hasAccess && !!id,
        }
    )

    const [notification, setNotification] = useState(null)

    const { mutate: retryBatch, isLoading: isRetrying } = useMutation(
        () => retryUberBatch(id),
        {
            onSuccess: () => {
                setNotification({
                    type: 'success',
                    title: i18n.t(`${T_PREFIX}.actions.retry_success_title`),
                    description: i18n.t(`${T_PREFIX}.actions.retry_success_description`),
                })
                refetch()
            },
            onError: (e) => {
                const msg = e?.response?.data?.error || i18n.t(`${T_PREFIX}.errors.retry_error`)
                setNotification({
                    type: 'error',
                    title: i18n.t(`${T_PREFIX}.errors.retry_error_title`),
                    description: msg,
                })
                // Refresh so a batch that became ineligible server-side (422)
                // clears the retry button immediately instead of on the next poll.
                refetch()
            },
        }
    )

    const canRetry = batch?.status === 'failed' && batch?.failure_reason === RETRYABLE_FAILURE_REASON

    const handleBack = () => {
        history.push(UBER_BATCHES.replace(':warehouse_id', warehouse_id))
    }

    const handleOpenOrder = (orderId) => {
        if (!orderId) return
        const target = SHIPPED_ORDER_DETAIL
            .replace(':warehouse_id', warehouse_id)
            .replace(':id', orderId)
        history.push(target)
    }

    const recipientName = (order) => {
        if (!order) return '-'
        const parts = [order.shipping_first_name, order.shipping_last_name].filter(Boolean)
        return parts.length ? parts.join(' ') : '-'
    }

    const deliveryColumns = useMemo(() => [
        {
            title: i18n.t(`${T_PREFIX}.detail.deliveries.uber_delivery_id`),
            dataIndex: 'uber_delivery_id',
            key: 'uber_delivery_id',
            render: (val) => <span className="font-mono text-xs">{val || '-'}</span>,
        },
        {
            title: i18n.t(`${T_PREFIX}.detail.deliveries.shipping_number`),
            key: 'shipping_number',
            render: (_, delivery) => (
                <span className="text-sm text-gray-700">
                    {delivery.shipping_label?.shipping_number || '-'}
                </span>
            ),
        },
        {
            title: i18n.t(`${T_PREFIX}.detail.deliveries.recipient`),
            key: 'recipient',
            render: (_, delivery) => (
                <span className="text-sm text-gray-700">{recipientName(delivery.order)}</span>
            ),
        },
        {
            title: i18n.t(`${T_PREFIX}.detail.deliveries.shipping_city`),
            key: 'shipping_city',
            render: (_, delivery) => (
                <span className="text-sm text-gray-700">{delivery.order?.shipping_city || '-'}</span>
            ),
        },
        {
            title: i18n.t(`${T_PREFIX}.detail.deliveries.status`),
            dataIndex: 'status',
            key: 'status',
            render: (status) => <span className="text-sm">{status || '-'}</span>,
        },
        {
            title: i18n.t(`${T_PREFIX}.detail.deliveries.return_pin_code`),
            dataIndex: 'return_pin_code',
            key: 'return_pin_code',
            render: (val) => <span className="font-mono text-sm text-gray-700">{val || '-'}</span>,
        },
        {
            title: i18n.t(`${T_PREFIX}.detail.deliveries.failed_reason`),
            dataIndex: 'failed_reason',
            key: 'failed_reason',
            render: (reason) => <span className="text-sm text-gray-600">{reason || '-'}</span>,
        },
        {
            title: i18n.t(`${T_PREFIX}.detail.deliveries.actions`),
            key: 'actions',
            render: (_, delivery) => {
                const orderId = delivery.order?.id || delivery.shipping_label?.order_id
                if (!orderId) return null
                return (
                    <button
                        onClick={() => handleOpenOrder(orderId)}
                        className="text-sm text-indigo-600 hover:text-indigo-800 underline"
                    >
                        {i18n.t(`${T_PREFIX}.detail.deliveries.view_order`)}
                    </button>
                )
            },
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [i18n, warehouse_id])

    if (!hasAccess) {
        return (
            <PageView>
                <div className="text-center py-12">
                    <p className="text-gray-500">{i18n.t(`${T_PREFIX}.errors.no_access`)}</p>
                </div>
            </PageView>
        )
    }

    return (
        <PageView
            topMenu={
                <PageTopBar>
                    <div className="flex items-center gap-3">
                        <Button onClick={handleBack} type="secondary">
                            {i18n.t(`${T_PREFIX}.detail.back`)}
                        </Button>
                        <div className="text-lg font-semibold">
                            {i18n.t(`${T_PREFIX}.detail.title`, { id })}
                        </div>
                        {canRetry && (
                            <Button
                                onClick={() => retryBatch()}
                                disabled={isRetrying}
                                loading={isRetrying}
                            >
                                {i18n.t(`${T_PREFIX}.actions.retry`)}
                            </Button>
                        )}
                    </div>
                </PageTopBar>
            }
            childrenFullWidth={true}
            topMenuFullWidth={true}
        >
            <>
                {isLoading && !batch ? (
                    <Loader show={true} />
                ) : isError ? (
                    <div className="text-center py-12">
                        <p className="text-red-500">
                            {error?.message || i18n.t(`${T_PREFIX}.errors.fetch_error`)}
                        </p>
                    </div>
                ) : !batch ? (
                    <div className="text-center py-12">
                        <p className="text-gray-500">{i18n.t(`${T_PREFIX}.detail.not_found`)}</p>
                    </div>
                ) : (
                    <>
                        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <DetailField
                                    label={i18n.t(`${T_PREFIX}.detail.fields.id`)}
                                    value={`#${batch.id}`}
                                />
                                <DetailField
                                    label={i18n.t(`${T_PREFIX}.detail.fields.status`)}
                                    value={<UberBatchStatusPill status={batch.status} />}
                                />
                                <DetailField
                                    label={i18n.t(`${T_PREFIX}.detail.fields.warehouse`)}
                                    value={batch.warehouse_name || warehouse?.name || `#${batch.warehouse_id}`}
                                />
                                <DetailField
                                    label={i18n.t(`${T_PREFIX}.detail.fields.dispatch_window_start`)}
                                    value={<FormattedDate date={batch.dispatch_window_start} shortDate />}
                                />
                                <DetailField
                                    label={i18n.t(`${T_PREFIX}.detail.fields.dispatch_window_end`)}
                                    value={<FormattedDate date={batch.dispatch_window_end} shortDate />}
                                />
                                <DetailField
                                    label={i18n.t(`${T_PREFIX}.detail.fields.submitted_at`)}
                                    value={<FormattedDate date={batch.submitted_at} shortDate />}
                                />
                                <DetailField
                                    label={i18n.t(`${T_PREFIX}.detail.fields.failed_at`)}
                                    value={<FormattedDate date={batch.failed_at} shortDate />}
                                />
                                <DetailField
                                    label={i18n.t(`${T_PREFIX}.detail.fields.deliveries_count`)}
                                    value={batch.deliveries_count ?? (batch.deliveries?.length ?? 0)}
                                />
                                {batch.failure_reason ? (
                                    <DetailField
                                        label={i18n.t(`${T_PREFIX}.detail.fields.failure_reason`)}
                                        value={<span className="text-red-700">{batch.failure_reason}</span>}
                                    />
                                ) : null}
                                {batch.internal_label ? (
                                    <DetailField
                                        label={i18n.t(`${T_PREFIX}.detail.fields.internal_label`)}
                                        value={<span className="font-mono">{batch.internal_label}</span>}
                                    />
                                ) : null}
                                {batch.uber_batch_id ? (
                                    <DetailField
                                        label={i18n.t(`${T_PREFIX}.detail.fields.uber_batch_id`)}
                                        value={<span className="font-mono">{batch.uber_batch_id}</span>}
                                    />
                                ) : null}
                                {batch.last_webhook_at ? (
                                    <DetailField
                                        label={i18n.t(`${T_PREFIX}.detail.fields.last_webhook_at`)}
                                        value={<FormattedDate date={batch.last_webhook_at} shortDate />}
                                    />
                                ) : null}
                            </div>
                        </div>

                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                            <div className="text-md font-semibold mb-3">
                                {i18n.t(`${T_PREFIX}.detail.deliveries.title`)}
                            </div>
                            <Table
                                rowKey="id"
                                columns={deliveryColumns}
                                dataSource={batch.deliveries || []}
                                pagination={false}
                                locale={{
                                    emptyText: i18n.t(`${T_PREFIX}.detail.deliveries.empty`),
                                }}
                            />
                        </div>
                    </>
                )}
                <Notification
                    show={!!notification}
                    setShow={() => setNotification(null)}
                    type={notification?.type || 'success'}
                    title={notification?.title || ''}
                    description={notification?.description || ''}
                />
            </>
        </PageView>
    )
}

const DetailField = ({ label, value }) => (
    <div>
        <div className="text-xs uppercase text-gray-500 tracking-wide">{label}</div>
        <div className="text-sm text-gray-900 mt-1">{value || '-'}</div>
    </div>
)
